import React, { useEffect, useState } from 'react'
import { Drawer } from 'rsuite'
import { CapsuleInfo } from '../pages/business-accounts/diagnostics'
import { RowDataType } from 'rsuite/esm/Table'
import { CapsuleTitleInfo } from './utils'
import { t } from '../utils/i18n'
import { ReactComponent as Close } from '../assets/svgs/modal-close.svg'
import { Button } from '../components/atoms/button/button'
import { useAuth } from '../customHooks/useAuth'
import { BUSINESS_DETAILS } from '../constants/routes'
import axios from '../utils/axios'
import { CanBeAnything, DeboardTypes } from '../types'
import { AssetMetaCard } from '../components/atoms/asset-meta-card/asset-meta-card'
import { showToast } from '../components/atoms/toaster/toaster'
import { AssetInfo } from '../components/organisms/asset-info/asset-info'
import { DeboardModal } from '../components/organisms/DeboardModal/deboard-modal'

interface AssetDrawerContainerProps {
  selectedCapsuleInfo: CapsuleInfo
  selectedAsset: RowDataType
  businessId: string
  onDrawerClose: () => void
  callAccountAPIonParent?: () => void
}

export const AssetDrawerContainer = ({
  selectedCapsuleInfo,
  selectedAsset,
  businessId,
  onDrawerClose,
  callAccountAPIonParent,
}: AssetDrawerContainerProps) => {
  const { user } = useAuth()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [, setIsLoading] = useState(false)
  const [assetDetail, setAssetDetail] = useState<CanBeAnything>({})
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  let modalTitle = ''
  const generateTitleTextAndId = () => {
    let str = ''
    let id = ''
    let type: DeboardTypes = DeboardTypes.CATALOG
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.FACEBOOK_PAGES) {
      str = `${t('FBPageId')}: ${selectedAsset.pageId}`
      id = selectedAsset?.pageId
      modalTitle = `${t('deboardfbtitle')}`
      type = DeboardTypes.FACEBOOK_PAGES
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.INSTAGRAM_ACCOUNTS) {
      str = `${t('igAccountId')}: ${selectedAsset.instaId}`
      id = selectedAsset?.instaId
      modalTitle = `${t('deboardigtitle')}`
      type = DeboardTypes.INSTAGRAM_ACCOUNTS
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.DATASET) {
      str = `${t('datasetId')}: ${selectedAsset.datasetId}`
      id = selectedAsset?.datasetId
      modalTitle = `${t('deboarddstitle')}`
      type = DeboardTypes.DATASET
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.CATALOGUE) {
      str = `${t('catalogueId')}: ${selectedAsset.catalogId}`
      id = selectedAsset?.catalogId
      modalTitle = `${t('deboardcatalogtitle')}`
      type = DeboardTypes.CATALOG
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.OWNED_ACCOUNTS) {
      str = `${t('accountID')}: ${selectedAsset.accountId}`
      id = selectedAsset?.accountId
      modalTitle = `${t('deboardaccount')}`
      type = DeboardTypes.ACCOUNT
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.CLIENT_ACCOUNTS) {
      str = `${t('accountID')}: ${selectedAsset.accountId}`
      id = selectedAsset?.accountId
      modalTitle = `${t('deboardaccount')}`
      type = DeboardTypes.ACCOUNT
    }
    return {
      str,
      id,
      type,
    }
  }

  const { id: accountId, str: accountStr, type: deboardApiType } = generateTitleTextAndId()

  useEffect(() => {
    if (Object.keys(selectedAsset).length) {
      setDrawerOpen(true)
    }

    return () => {
      setAssetDetail({})
    }
  }, [selectedAsset])

  const callDetailAPI = async (parent: string) => {
    try {
      setIsLoading(true)
      const config = {
        method: 'GET',
        url: `${BUSINESS_DETAILS}/${businessId}/${parent}/${accountId}/details`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const details = await axios(config)

      return details?.data?.data
    } catch {
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const onDrawerOpenHandler = async () => {
    let detail: CanBeAnything
    if (selectedCapsuleInfo.title === CapsuleTitleInfo.FACEBOOK_PAGES) {
      detail = await callDetailAPI('page')
    }
    if (selectedCapsuleInfo.title === CapsuleTitleInfo.INSTAGRAM_ACCOUNTS) {
      detail = await callDetailAPI('insta')
    }
    if (selectedCapsuleInfo.title === CapsuleTitleInfo.DATASET) {
      detail = await callDetailAPI('dataset')
    }
    if (selectedCapsuleInfo.title === CapsuleTitleInfo.CATALOGUE) {
      detail = await callDetailAPI('catalog')
    }
    if (
      selectedCapsuleInfo.title === CapsuleTitleInfo.OWNED_ACCOUNTS ||
      selectedCapsuleInfo.title === CapsuleTitleInfo.CLIENT_ACCOUNTS
    ) {
      detail = await callDetailAPI('adaccount')
    }
    setAssetDetail(detail)
  }

  const renderCards = () => {
    const cardDetails = []
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.FACEBOOK_PAGES) {
      cardDetails.push(
        {
          label: 'pageSync',
          ...assetDetail?.pageSync,
        },
        { label: 'pageUsersSync', ...assetDetail?.pageUsersSync }
      )
      return (
        <>
          {cardDetails.map((detail, idx) => {
            return <AssetMetaCard detail={detail} key={idx} />
          })}
        </>
      )
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.INSTAGRAM_ACCOUNTS) {
      cardDetails.push({
        label: 'instaSync',
        ...assetDetail?.instaSync,
      })
      return (
        <>
          {cardDetails.map((detail, idx) => {
            return <AssetMetaCard detail={detail} key={idx} />
          })}
        </>
      )
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.DATASET) {
      cardDetails.push(
        {
          label: 'datasetSync',
          ...assetDetail?.datasetSync,
        },
        { label: 'datasetUserSync', ...assetDetail?.datasetUserSync }
      )
      return (
        <>
          {cardDetails.map((detail, idx) => {
            return <AssetMetaCard detail={detail} key={idx} />
          })}
        </>
      )
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.CATALOGUE) {
      cardDetails.push(
        {
          label: 'catalogSync',
          ...assetDetail?.catalogSync,
        },
        { label: 'catalogUserSync', ...assetDetail?.catalogUserSync }
      )
      return (
        <>
          {cardDetails.map((detail, idx) => {
            return <AssetMetaCard detail={detail} key={idx} />
          })}
        </>
      )
    }
  }
  const getBtnName = () => {
    let str = ''
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.FACEBOOK_PAGES) {
      str = t('deboardFbBtn')
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.INSTAGRAM_ACCOUNTS) {
      str = t('deboardIgBtn')
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.DATASET) {
      str = t('deboardDsBtn')
    }
    if (selectedCapsuleInfo?.title === CapsuleTitleInfo.CATALOGUE) {
      str = t('deboardCatalogBtn')
    }
    if (
      selectedCapsuleInfo?.title === CapsuleTitleInfo.OWNED_ACCOUNTS ||
      selectedCapsuleInfo?.title === CapsuleTitleInfo.CLIENT_ACCOUNTS
    ) {
      str = t('deboard')
    }
    return str
  }

  const callDeboardAPI = async (type: DeboardTypes, id: string, businessAccountId: string) => {
    try {
      const config = {
        method: 'POST',
        url: `${BUSINESS_DETAILS}/deboard/assets`,
        data: { type, id, businessAccountId },
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const result = await axios(config)

      return result
    } catch {
      console.log('Deboard error')
    }
  }

  const deboardAsset = async (id: string) => {
    // deboard api will come here.
    const result = await callDeboardAPI(deboardApiType, id, businessId)
    if (result?.data?.data?.success) {
      showToast({
        message: t('account.deboard.success').replace('{{asset-id}}', t(`${id}`)),
      })
      setDrawerOpen(false)
      callAccountAPIonParent?.()
    } else {
      showToast({
        message: t('account.deboard.fail').replace('{{asset-id}}', t(`${id}`)),
        isError: true,
      })
    }

    setModalOpen(false)
  }

  const btnName = getBtnName()
  return (
    <>
      <Drawer
        open={drawerOpen}
        onOpen={onDrawerOpenHandler}
        backdrop={true}
        onClose={() => setDrawerOpen(false)}
        onExit={onDrawerClose}
        keyboard={true}
        size={`${selectedCapsuleInfo?.title === CapsuleTitleInfo.OWNED_ACCOUNTS || selectedCapsuleInfo?.title === CapsuleTitleInfo.CLIENT_ACCOUNTS ? 'md' : 'sm'}`}
      >
        <div className="drawer-header-content-fluid">
          <Drawer.Title>
            <div className="drawer-header-content">
              <h3>{accountStr}</h3>
              <Button
                ButtonIcon={<Close />}
                removeDefaultCss
                ariaLabel="close"
                className="close-btn"
                handleClick={() => setDrawerOpen(false)}
              />
            </div>
          </Drawer.Title>
        </div>
        <p className="business-id-text">
          {t('businessAccountId')}: {businessId}
        </p>
        <div className="drawer-mid-section">
          <div className="drawer-body">
            {selectedCapsuleInfo?.title === CapsuleTitleInfo.OWNED_ACCOUNTS ||
            selectedCapsuleInfo?.title === CapsuleTitleInfo.CLIENT_ACCOUNTS ? (
              <AssetInfo assetDetail={assetDetail} />
            ) : (
              <>
                <h4 className="drawer-body-text">{t('diagnostics')}</h4>
                <div className="asset-cards">{renderCards()}</div>
              </>
            )}
          </div>
          <div className="deboard-btn">
            <Button
              removeDefaultCss
              ariaLabel="deboard"
              handleClick={() => setModalOpen(true)}
              label={btnName}
            />
          </div>
        </div>
      </Drawer>
      <DeboardModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        modalTitle={modalTitle}
        onDeboardClickHandler={() => deboardAsset(accountId)}
        modalMessage={t('deboardModalMsgAccount')}
      />
    </>
  )
}
